import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { logoHorizontalWhite } from '@/shared/assets'
import { LoadingDots } from '@/components/atoms'
import { ChevronRightIcon } from '@/components/atoms/icons'

export type IdCardLink = { label: string } & (
  | { to: string; onClick?: never }
  | { onClick: () => void; to?: never }
)

type WithPhotoProps = {
  variant: 'with-photo'
  photoUrl?: string
  planName: string
  ownerName: string
  hidePhotoBackground?: boolean
} & (
  | { link?: never; policyId: string; startDate: string }
  | { link?: IdCardLink; policyId?: never; startDate?: never }
)

type NoPhotoProps = {
  variant: 'no-photo'
  policyId: string
  birthday?: string
  weight?: string
  vetName?: string
  address?: string
}

type IdCardProps = (WithPhotoProps | NoPhotoProps) & {
  className?: string
  isMobile?: boolean
  isLoading?: boolean
}

const cardBase =
  'relative flex w-full overflow-hidden rounded-[1.25rem] border border-transparent backdrop-blur-[21px] after:absolute after:inset-0 after:bg-[linear-gradient(112deg,rgba(255,255,255,0.40)_0%,rgba(255,255,255,0.00)_101.12%)]'

const CARD_PADDING = 'p-[1.375rem_1.5rem_1.375rem_1.375rem]'

const GRADIENT =
  'linear-gradient(90deg, rgba(255,158,117,0.5) 8.65%, rgba(85,79,255,0.54) 52.58%, rgb(85,79,255) 100%)'

const LINK_CLASS =
  'relative z-10 flex items-center gap-1 text-xs leading-4 tracking-base text-white'

const cardGradient = {
  background: `${GRADIENT} padding-box, ${GRADIENT} border-box`,
}

export function IdCard({
  className = '',
  isMobile = false,
  isLoading = false,
  ...props
}: IdCardProps) {
  const isWithPhoto = props.variant === 'with-photo'
  const { t } = useTranslation()

  return (
    <div
      className={`${cardBase} ${
        isWithPhoto
          ? isMobile
            ? 'aspect-343/112 flex-row items-center gap-3 p-4'
            : `aspect-336/208 flex-col items-stretch gap-0 ${CARD_PADDING}`
          : isMobile
            ? 'aspect-343/112 flex-col p-4'
            : `aspect-336/208 flex-col ${CARD_PADDING}`
      } ${className}`}
      style={cardGradient}
    >
      <img
        src={logoHorizontalWhite}
        alt="Kanguro"
        className={`absolute right-6 w-auto ${isMobile ? 'top-4.5 h-[1.094rem]' : 'top-5.5 h-6.5'}`}
      />

      {isWithPhoto ? (
        <>
          <div
            className={`relative z-10 shrink-0 overflow-hidden rounded-full ${props.hidePhotoBackground ? '' : 'bg-indigo-100'} ${isMobile ? 'size-17.25' : 'size-20 sm:size-23'}`}
          >
            {props.photoUrl && (
              <img
                src={props.photoUrl}
                alt={props.ownerName}
                className="relative z-10 size-full object-cover"
              />
            )}
          </div>

          <div
            className={`relative z-10 flex flex-row justify-between gap-2 ${isMobile ? 'flex-1 items-center' : 'mt-auto w-full flex-none items-end'}`}
          >
            <div className="flex flex-col gap-0.5">
              <p
                className={`font-bold text-white ${!isLoading && props.planName.includes(' ') ? (isMobile ? 'text-lg' : 'mt-2.5 text-[1.5rem] leading-6') : isMobile ? 'text-xl' : 'text-[2rem]'}`}
              >
                {isLoading ? (
                  <LoadingDots className="text-white" />
                ) : (
                  props.planName
                )}
              </p>
              <p
                className={`text-white ${isMobile ? 'text-[0.875rem]' : 'text-base'}`}
              >
                {props.ownerName}
              </p>
            </div>
            {props.link ? (
              <IdCardLinkButton link={props.link} isMobile={isMobile} />
            ) : props.policyId ? (
              <div className="flex flex-col items-end gap-0.5">
                <p className="text-base font-bold text-white">
                  {props.policyId}
                </p>
                <p className="text-[0.75rem] text-white">
                  {t('common.starts')}:{' '}
                  {isLoading ? (
                    <LoadingDots className="text-white" />
                  ) : (
                    props.startDate
                  )}
                </p>
              </div>
            ) : (
              <></>
            )}
          </div>
        </>
      ) : (
        <>
          <p
            className={`font-bold text-white ${isMobile ? 'text-base' : 'text-2xl'}`}
          >
            {props.policyId}
          </p>

          <div className="mt-auto flex items-end justify-between gap-0.5 text-white">
            {(props.birthday || props.weight) && (
              <div className="flex w-full min-w-3/5 flex-col gap-0.5">
                {props.birthday && (
                  <p
                    className={`text-white ${isMobile ? 'text-[0.75rem]' : 'text-[0.875rem]'}`}
                  >
                    {t('idCard.birthday')}: {props.birthday}
                  </p>
                )}
                {props.weight && (
                  <p
                    className={`text-white ${isMobile ? 'text-[0.75rem]' : 'text-[0.875rem]'}`}
                  >
                    {t('idCard.currentWeight')}: {props.weight}
                  </p>
                )}
              </div>
            )}
            {props.vetName && (
              <p
                className={`line-clamp-2 w-full text-right text-white ${isMobile ? 'text-[0.75rem]' : 'text-[0.875rem]'}`}
              >
                {props.vetName}
              </p>
            )}
            {props.address && (
              <p
                className={`text-white ${isMobile ? 'text-[0.75rem]' : 'text-[0.875rem]'}`}
              >
                {t('idCard.address')}: {props.address}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function IdCardLinkButton({
  link,
  isMobile,
}: {
  link: IdCardLink
  isMobile: boolean
}) {
  const content = (
    <>
      <span className="underline">{link.label}</span>
      {!isMobile && <ChevronRightIcon className="h-4 w-4" />}
    </>
  )
  const className = `${LINK_CLASS} shrink-0 self-end ${!isMobile ? 'my-1' : ''}`

  return link.to ? (
    <Link to={link.to} className={className} data-testid="id-card-link">
      {content}
    </Link>
  ) : (
    <button
      type="button"
      onClick={link.onClick}
      className={className}
      data-testid="id-card-link"
    >
      {content}
    </button>
  )
}
